import { Suspense } from "react";
import { lazyWithReload } from "@/lib/lazyWithReload";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReportAuthProvider } from "@/components/admin/analytics/orgReportQuery";
import { shouldRetryQuery } from "@/lib/requireLiveSession";
import { Analytics } from "@vercel/analytics/react";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import GeoGate from "./components/auth/GeoGate";
import { AgentModeProvider } from "./hooks/useAgentMode";
import { ActiveOrgProvider } from "./hooks/useActiveOrg";
import { AgentOrgActionsProvider } from "./hooks/useAgentOrgActions";
import { CurrentUserProfileProvider } from "./hooks/useCurrentUserProfile";
import CompareTrayMount from "./components/explore/CompareTrayMount";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import FeatureRoute from "./components/auth/FeatureRoute";
import OrgFeatureRoute from "./components/auth/OrgFeatureRoute";
import SuperAdminRoute from "./components/auth/SuperAdminRoute";
import StaffRoute from "./components/auth/StaffRoute";
import { publicMarketingRoutes } from "./routes/publicMarketingRoutes";
import LandingPage from "./pages/LandingPage";
import PublicOrganizationProfile from "./pages/PublicOrganizationProfile";
import PublicOrganizationNewsArticle from "./pages/PublicOrganizationNewsArticle";
import OrganizationRouteSwitch from "./components/auth/OrganizationRouteSwitch";
import { ADMIN_HOME } from "@/lib/postLoginDestination";
import { BrandedLoadingSpinner } from "@/components/brand";
import AgentOnboardingGate from "./components/onboarding/AgentOnboardingGate";
import OrgOnboardingGate from "./components/onboarding/OrgOnboardingGate";

// App-only pages load on demand, so the homepage and other prerendered public
// pages do not download every signed-in surface before the header can show the
// Sign in button. Prerendered pages (publicMarketingRoutes, LandingPage and the
// public organization pages) stay eager: the client remounts instead of
// hydrating, so a lazy one would blank its prerendered markup while it loads.
const FeedPage = lazyWithReload(() => import("./pages/FeedPage"));
const CommunityPage = lazyWithReload(() => import("./pages/CommunityPage"));
const EventsPage = lazyWithReload(() => import("./pages/EventsPage"));
const NetworkPage = lazyWithReload(() => import("./pages/NetworkPage"));
const AgentDashboard = lazyWithReload(() => import("./pages/AgentDashboard"));
const OrganizationProfile = lazyWithReload(() => import("./pages/OrganizationProfile"));
const OrganizationNewsArticle = lazyWithReload(() => import("./pages/OrganizationNewsArticle"));
const ComparePage = lazyWithReload(() => import("./pages/ComparePage"));
const MyBrokeragePage = lazyWithReload(() => import("./pages/MyBrokeragePage"));
const AdminDashboard = lazyWithReload(() => import("./pages/AdminDashboard"));
const AdminSetupPage = lazyWithReload(() => import("./pages/AdminSetupPage"));
const AdminFeedPage = lazyWithReload(() => import("./pages/AdminFeedPage"));
const ContentPublishingPage = lazyWithReload(() => import("./pages/ContentPublishingPage"));
const ExplorePage = lazyWithReload(() => import("./pages/ExplorePage"));
const ForTeamsPage = lazyWithReload(() => import("./pages/ForTeamsPage"));
const EditProfilePage = lazyWithReload(() => import("./pages/EditProfilePage"));
const AnalyticsPage = lazyWithReload(() => import("./pages/AnalyticsPage"));
const RankingsPage = lazyWithReload(() => import("./pages/RankingsPage"));
const MessagesPage = lazyWithReload(() => import("./pages/MessagesPage"));
const AgentMessagesPage = lazyWithReload(() => import("./pages/AgentMessagesPage"));
const MyReviewsPage = lazyWithReload(() => import("./pages/MyReviewsPage"));
const AgentProfilePage = lazyWithReload(() => import("./pages/AgentProfilePage"));
const AgentSettingsPage = lazyWithReload(() => import("./pages/AgentSettingsPage"));
const TeamManagementPage = lazyWithReload(() => import("./pages/TeamManagementPage"));
const BillingPage = lazyWithReload(() => import("./pages/BillingPage"));
const SettingsPage = lazyWithReload(() => import("./pages/SettingsPage"));
const OrgSettingsPage = lazyWithReload(() => import("./pages/OrgSettingsPage"));
const FounderInvitePage = lazyWithReload(() => import("./pages/FounderInvitePage"));
const ConsoleAccountPage = lazyWithReload(() => import("./pages/console/ConsoleAccountPage"));
const ConsoleFeedPage = lazyWithReload(() => import("./pages/console/ConsoleFeedPage"));
const ConsoleInvitePage = lazyWithReload(() => import("./pages/console/ConsoleInvitePage"));
const ConsoleActivationPage = lazyWithReload(() => import("./pages/console/ConsoleActivationPage"));
const ConsoleCustomToolsPage = lazyWithReload(() => import("./pages/console/ConsoleCustomToolsPage"));
const ConsoleOrgBuildPage = lazyWithReload(() => import("./pages/console/ConsoleOrgBuildPage"));
const ConsoleOrgEditPage = lazyWithReload(() => import("./pages/console/ConsoleOrgEditPage"));
const ConsoleOrganizationsPage = lazyWithReload(() => import("./pages/console/ConsoleOrganizationsPage"));
const ConsolePipelinePage = lazyWithReload(() => import("./pages/console/ConsolePipelinePage"));
const ConsoleImportPage = lazyWithReload(() => import("./pages/console/ConsoleImportPage"));
const ConsolePeoplePage = lazyWithReload(() => import("./pages/console/ConsolePeoplePage"));
const ConsoleWaitlistPage = lazyWithReload(() => import("./pages/console/ConsoleWaitlistPage"));
const ConsoleClaimsPage = lazyWithReload(() => import("./pages/console/ConsoleClaimsPage"));
const ConsolePlaceholderPage = lazyWithReload(() => import("./pages/console/ConsolePlaceholderPage"));
const ResetPasswordPage = lazyWithReload(() => import("./pages/ResetPasswordPage"));
const AgentWelcomePage = lazyWithReload(() => import("./pages/AgentWelcomePage"));
const OpportunitiesPage = lazyWithReload(() => import("./pages/OpportunitiesPage"));
const NotFound = lazyWithReload(() => import("./pages/NotFound"));
const AcceptInvitePage = lazyWithReload(() => import("./pages/AcceptInvitePage"));
const ReviewTokenPage = lazyWithReload(() => import("./pages/ReviewTokenPage"));
const AuthCallback = lazyWithReload(() => import("./pages/AuthCallback"));
const SocialAuthCallback = lazyWithReload(() => import("./pages/SocialAuthCallback"));
const PublicAgentProfilePage = lazyWithReload(() => import("./pages/PublicAgentProfilePage"));
const ClaimPage = lazyWithReload(() => import("./pages/ClaimPage"));
const OrgClaimPage = lazyWithReload(() => import("./pages/OrgClaimPage"));
const ActivatePage = lazyWithReload(() => import("./pages/ActivatePage"));
const UnsubscribePage = lazyWithReload(() => import("./pages/UnsubscribePage"));
// Isolated primitives showcase — verification route only. Not linked from any
// nav; nothing consumes the primitives in a live surface yet.
const PrimitivesShowcase = lazyWithReload(() => import("./pages/PrimitivesShowcase"));

// ANY configured account, not all of them — the widget renders each account
// independently (src/lib/demoLoginAccounts.ts), and CI builds with only the
// agent credential. Kept as a direct `import.meta.env` expression rather than a
// call to resolveDemoAccounts so Vite can still fold it to `false` statically
// and tree-shake the lazy chunk out of production entirely.
const demoLoginEnabled =
  !!import.meta.env.VITE_DEMO_AGENT_PASSWORD ||
  !!import.meta.env.VITE_DEMO_ADMIN_PASSWORD;
const DemoLogin = demoLoginEnabled
  ? lazyWithReload(() => import("./components/auth/DemoLogin"))
  : null;

const RouteChunkFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <BrandedLoadingSpinner size="lg" />
  </div>
);

// Queries skipped for lack of a live session are not retried (#794).
const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: shouldRetryQuery } },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
      <Toaster />
      <Sonner />
      <Analytics />
      {/* One auth read for every org-report hook, and the guard that drops
          every org-report cache entry when the signed-in user changes or
          signs out (issue #872). */}
      <ReportAuthProvider>
      <GeoGate>
        <BrowserRouter>
          <AgentModeProvider>
          <ActiveOrgProvider>
          <AgentOrgActionsProvider>
          {/* Owns the single profile fetch + realtime channel for the signed-in
              user. Must wrap every consumer: the channel topic is derived from
              the user id, so a per-component subscription registers the same
              topic repeatedly, which supabase-js >=2.112 throws on. */}
          <CurrentUserProfileProvider>
          <ScrollToTop />
          {/* One boundary for every lazy page: shown only while a page's chunk
              downloads for the first time. */}
          <Suspense fallback={<RouteChunkFallback />}>
          <Routes>
          {/* Public routes. The marketing + legal set lives in its own module
              because it is rendered twice: here for the SPA, and by
              src/entry-prerender.tsx to build the static HTML crawlers get. */}
          {publicMarketingRoutes()}
          <Route path="/landing" element={<LandingPage />} />
          {/* Legacy redirects (preserve any inbound links) */}
          <Route path="/for-agents" element={<Navigate to="/agents" replace />} />
          <Route path="/for-organizations" element={<Navigate to="/teams-brokerages" replace />} />
          <Route path="/for-teams-old" element={<ForTeamsPage />} />
          <Route path="/invite" element={<AcceptInvitePage />} />
          <Route path="/review/:token" element={<ReviewTokenPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Founder-created agent invitation landing. Unauthenticated by
              design — the invitee has no session until they redeem here. Kept
              out of publicMarketingRoutes: it is an app route, not a
              prerendered marketing page. */}
          <Route path="/welcome" element={<AgentWelcomePage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          {/* Social connect landing (#41). Deliberately NOT wrapped in
              ProtectedRoute: the page settles the session itself so it can render
              a controlled outcome for no session or the wrong session, rather
              than bouncing a returning user away mid-flow. Not prerendered and
              not sitemapped — it is an app route, and it is noindex. */}
          <Route path="/auth/social/callback" element={<SocialAuthCallback />} />
          <Route path="/agents/:slug" element={<PublicAgentProfilePage />} />
          <Route path="/claim" element={<ClaimPage />} />
          {/* "Claim this profile" for a basic listing. Not ProtectedRoute: a
              signed-out visitor sees what claiming involves and signs up or in
              here. Signed in, every decision is the server's. Off
              (FEATURE_FLAGS.ORG_CLAIM_REQUESTS), it redirects to the
              organization page. noindex; not prerendered or sitemapped. */}
          <Route path="/claim-organization/:slug" element={<OrgClaimPage />} />
          {/* Bound org-admin invitation landing (Slice 5). Public, ungated,
              noindex; the token lives in module memory, never the URL. Not
              prerendered and not sitemapped. */}
          <Route path="/activate" element={<ActivatePage />} />
          {/* Email unsubscribe confirmation (#1062). Public, ungated, noindex;
              opening it writes nothing, its button POSTs to nudge-unsubscribe.
              Not prerendered and not sitemapped. */}
          <Route path="/unsubscribe" element={<UnsubscribePage />} />

          {/* Isolated, unlinked verification route for the shared UI primitives. */}
          <Route
            path="/_showcase/primitives"
            element={
              <Suspense fallback={null}>
                <PrimitivesShowcase />
              </Suspense>
            }
          />

          {/* Authenticated routes (any role) */}
          {/* Shared social spaces (both agents and admins) */}
          <Route path="/feed" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
          <Route path="/community" element={<ProtectedRoute requiredRole="agent"><FeatureRoute featureKey="community"><CommunityPage /></FeatureRoute></ProtectedRoute>} />

          {/* Agent-only tools */}
          <Route path="/agent/dashboard" element={<ProtectedRoute requiredRole="agent"><FeatureRoute featureKey="dashboard"><AgentDashboard /></FeatureRoute></ProtectedRoute>} />
          <Route path="/agent/opportunities" element={<ProtectedRoute requiredRole="agent"><FeatureRoute featureKey="opportunities"><OpportunitiesPage /></FeatureRoute></ProtectedRoute>} />
          <Route path="/agent/feed" element={<ProtectedRoute requiredRole="agent"><FeatureRoute featureKey="feed"><FeedPage /></FeatureRoute></ProtectedRoute>} />
          <Route path="/agent/community" element={<ProtectedRoute requiredRole="agent"><FeatureRoute featureKey="community"><CommunityPage /></FeatureRoute></ProtectedRoute>} />
          <Route path="/agent/profile" element={<ProtectedRoute requiredRole="agent"><AgentProfilePage /></ProtectedRoute>} />
          <Route path="/agent/explore" element={<ProtectedRoute requiredRole="agent"><FeatureRoute featureKey="explore"><ExplorePage /></FeatureRoute></ProtectedRoute>} />
          <Route path="/agent/compare" element={<ProtectedRoute requiredRole="agent"><ComparePage /></ProtectedRoute>} />
          <Route path="/agent/my-brokerage" element={<ProtectedRoute requiredRole="agent"><MyBrokeragePage /></ProtectedRoute>} />
          <Route path="/agent/network" element={<ProtectedRoute requiredRole="agent"><FeatureRoute featureKey="network"><NetworkPage /></FeatureRoute></ProtectedRoute>} />
          <Route path="/agent/settings" element={<ProtectedRoute requiredRole="agent"><AgentSettingsPage /></ProtectedRoute>} />
          <Route path="/agent/events" element={<ProtectedRoute requiredRole="agent"><FeatureRoute featureKey="events"><EventsPage /></FeatureRoute></ProtectedRoute>} />
          <Route path="/agent/messages" element={<ProtectedRoute requiredRole="agent" allowWithoutOrg><AgentMessagesPage /></ProtectedRoute>} />
          <Route path="/agent/reviews" element={<ProtectedRoute requiredRole="agent" allowWithoutOrg><MyReviewsPage /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute requiredRole="agent"><FeatureRoute featureKey="dashboard"><AgentDashboard /></FeatureRoute></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><AgentProfilePage /></ProtectedRoute>} />
          {/*
            Organization profiles are PUBLIC and crawlable: an anonymous visitor
            (or crawler) gets the lean public page, built from the anonymous
            allowlist RPC and prerendered to static HTML at build time; a
            signed-in user gets the full gated profile at the same URL. See
            src/routes/publicOrganizationRoutes.tsx and OrganizationRouteSwitch.
          */}
          <Route
            path="/organization/:slug"
            element={
              <OrganizationRouteSwitch
                publicElement={<PublicOrganizationProfile />}
                authenticated={<ProtectedRoute><OrganizationProfile /></ProtectedRoute>}
              />
            }
          />
          <Route
            path="/organization/:slug/news/:contentId"
            element={
              <OrganizationRouteSwitch
                publicElement={<PublicOrganizationNewsArticle />}
                authenticated={<ProtectedRoute><OrganizationNewsArticle /></ProtectedRoute>}
              />
            }
          />
          <Route path="/explore" element={<ProtectedRoute requiredRole="agent"><FeatureRoute featureKey="explore"><ExplorePage /></FeatureRoute></ProtectedRoute>} />
          <Route path="/compare" element={<ProtectedRoute requiredRole="agent"><ComparePage /></ProtectedRoute>} />
          <Route path="/my-brokerage" element={<ProtectedRoute requiredRole="agent"><MyBrokeragePage /></ProtectedRoute>} />
          <Route path="/network" element={<ProtectedRoute requiredRole="agent"><FeatureRoute featureKey="network"><NetworkPage /></FeatureRoute></ProtectedRoute>} />
          <Route path="/opportunities" element={<ProtectedRoute requiredRole="agent"><FeatureRoute featureKey="opportunities"><OpportunitiesPage /></FeatureRoute></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute requiredRole="agent"><AgentSettingsPage /></ProtectedRoute>} />
          <Route path="/events" element={<ProtectedRoute><FeatureRoute featureKey="events"><EventsPage /></FeatureRoute></ProtectedRoute>} />

          {/* Internal operations Console — staff-only (public.staff_members via
              is_staff()). Deliberately NOT under /admin/*: that namespace is the
              ORG-admin app (requiredRole="admin"), and the Console is
              staff-facing. Each page's data comes from the console-* edge
              functions, which re-check staff membership server-side; these
              guards are convenience only. */}
          <Route path="/console" element={<Navigate to="/console/people" replace />} />
          <Route path="/console/people" element={<StaffRoute><ConsolePeoplePage /></StaffRoute>} />
          <Route path="/console/waitlist" element={<StaffRoute><ConsoleWaitlistPage /></StaffRoute>} />
          <Route path="/console/feed" element={<StaffRoute><ConsoleFeedPage /></StaffRoute>} />
          {/* Staff-gated like its siblings; the page narrows to founder-only on
              top, since minting an invite is a stricter bar than reaching the
              Console. Shares its form with /admin/invite via InvitePanel. */}
          <Route path="/console/invite" element={<StaffRoute><ConsoleInvitePage /></StaffRoute>} />
          {/* One route, not /console/accounts/:email — the target account is
              carried in router state so a customer's email never reaches the URL
              bar, history, or a referrer header. Landing here without that state
              redirects to the roster. */}
          <Route path="/console/accounts" element={<StaffRoute><ConsoleAccountPage /></StaffRoute>} />
          <Route path="/console/organizations" element={<StaffRoute><ConsoleOrganizationsPage /></StaffRoute>} />
          {/* Claim requests (#498): list for staff, approve/decline founder-only,
              all through console-org-claims (staff + super_admins re-checked). */}
          <Route path="/console/claims" element={<StaffRoute><ConsoleClaimsPage /></StaffRoute>} />
          {/* Staff edit-on-behalf. Writes go through console-org-edit (staff-
              gated, reason-required, audited) — never the table directly. */}
          <Route path="/console/organizations/:orgId/edit" element={<StaffRoute><ConsoleOrgEditPage /></StaffRoute>} />
          {/* Staff build-out. Reads and writes go through console-org-enrichment
              (staff-gated, audited) — suggestions are staged for the owner to
              review, never written straight onto the profile. */}
          <Route path="/console/organizations/:orgId/build" element={<StaffRoute><ConsoleOrgBuildPage /></StaffRoute>} />
          {/* Prebuilt-organization pipeline: console-org-enrichment action "list"
              (staff-gated, read-only). Resend stays founder-only (console-resend). */}
          <Route path="/console/pipeline" element={<StaffRoute><ConsolePipelinePage /></StaffRoute>} />
          <Route path="/console/import" element={<StaffRoute><ConsoleImportPage /></StaffRoute>} />
          <Route path="/console/activation" element={<StaffRoute><ConsoleActivationPage /></StaffRoute>} />
          <Route path="/console/tools" element={<StaffRoute><ConsoleCustomToolsPage /></StaffRoute>} />
          <Route
            path="/console/support"
            element={
              <StaffRoute>
                <ConsolePlaceholderPage
                  title="Support"
                  description="Look up an account and resolve what someone is stuck on, in one place."
                />
              </StaffRoute>
            }
          />
          <Route
            path="/console/analytics"
            element={
              <StaffRoute>
                <ConsolePlaceholderPage
                  title="Analytics"
                  description="Platform-wide activity across orgs and agents, separate from the per-org analytics an org admin sees."
                />
              </StaffRoute>
            }
          />
          <Route
            path="/console/settings"
            element={
              <StaffRoute>
                <ConsolePlaceholderPage
                  title="Settings · Access"
                  description="Who can reach this console, and what each staff role is scoped to. Access is founder-only until this ships."
                />
              </StaffRoute>
            }
          />

          {/* Admin-only routes */}
          {/* Founder-only. Gated on super-admin (NOT requiredRole="admin"), so an
              agent-role founder isn't bounced to /admin/setup. The page re-checks too. */}
          <Route path="/admin/invite" element={<SuperAdminRoute><FounderInvitePage /></SuperAdminRoute>} />
          <Route path="/admin/setup" element={<ProtectedRoute skipOrgCheck><AdminSetupPage /></ProtectedRoute>} />
          <Route path="/admin" element={<Navigate to={ADMIN_HOME} replace />} />
          <Route path="/admin/dashboard" element={<ProtectedRoute requiredRole="admin"><OrgFeatureRoute featureKey="showOrgDashboard"><AdminDashboard /></OrgFeatureRoute></ProtectedRoute>} />
          <Route path="/admin/feed" element={<ProtectedRoute requiredRole="admin"><AdminFeedPage /></ProtectedRoute>} />
          <Route path="/admin/content" element={<ProtectedRoute requiredRole="admin" allowOrgRoles={["editor"]}><ContentPublishingPage /></ProtectedRoute>} />
          <Route path="/admin/profile" element={<ProtectedRoute requiredRole="admin" allowOrgRoles={["editor"]}><EditProfilePage /></ProtectedRoute>} />
          <Route path="/admin/analytics" element={<ProtectedRoute requiredRole="admin"><OrgFeatureRoute featureKey="showOrgAnalytics"><AnalyticsPage /></OrgFeatureRoute></ProtectedRoute>} />
          <Route path="/admin/rankings" element={<ProtectedRoute requiredRole="admin"><OrgFeatureRoute featureKey="showOrgRankings"><RankingsPage /></OrgFeatureRoute></ProtectedRoute>} />
          <Route path="/admin/messages" element={<ProtectedRoute requiredRole="admin"><MessagesPage /></ProtectedRoute>} />
          <Route path="/admin/team-management" element={<ProtectedRoute requiredRole="admin"><TeamManagementPage /></ProtectedRoute>} />
          <Route path="/admin/billing" element={<ProtectedRoute requiredRole="admin"><OrgFeatureRoute featureKey="showOrgBilling"><BillingPage /></OrgFeatureRoute></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute requiredRole="admin"><SettingsPage /></ProtectedRoute>} />
          <Route path="/admin/organization" element={<ProtectedRoute requiredRole="admin"><OrgSettingsPage /></ProtectedRoute>} />

          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          {/* App-level onboarding mounts; each self-gates by role + agent mode,
              so they are mutually exclusive (agent wizard in agent mode, org
              wizard in admin context) and inert for everyone else. */}
          <AgentOnboardingGate />
          <OrgOnboardingGate />
          {/* App-level compare tray — self-gates to the agent experience and
              renders nothing until an org is selected. */}
          <CompareTrayMount />
          {DemoLogin && (
            <Suspense fallback={null}>
              <DemoLogin />
            </Suspense>
          )}
          </CurrentUserProfileProvider>
          </AgentOrgActionsProvider>
          </ActiveOrgProvider>
          </AgentModeProvider>
        </BrowserRouter>
      </GeoGate>
      </ReportAuthProvider>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
